<template>
	<section class="saved-searches-panel">
		<header class="header">
			<button type="button" class="back-button" :title="t('custom-views.back')" @click="$emit('close')">
				<AppIcon :name="IconName.ChevronLeft" />
			</button>
			<h2>{{ t("custom-views.title") }}</h2>
			<span class="count-pill">{{ savedSearches.length }} / 20</span>
		</header>

		<label class="search-input">
			<AppIcon :name="IconName.Search" />
			<input v-model.trim="savedSearchQuery" type="search" :placeholder="t('custom-views.search-placeholder')" />
		</label>

		<p v-if="isLoading" class="state-message">{{ t("custom-views.loading") }}</p>
		<p v-else-if="loadError" class="error-message">{{ loadError }}</p>
		<p v-else-if="filteredSavedSearches.length === 0" class="state-message">{{ t("custom-views.empty") }}</p>

		<div v-else class="list">
			<div
				v-for="view in filteredSavedSearches"
				:key="view.id"
				role="button"
				tabindex="0"
				class="item"
				:class="{ active: activeSavedSearchId === view.id }"
				@click="$emit('select', view)"
				@keydown.enter.prevent="$emit('select', view)"
				@keydown.space.prevent="$emit('select', view)"
			>
				<div class="main">
					<div class="title-row">
						<input
							v-if="editingSavedSearchId === view.id"
							:ref="assignRenameInput"
							v-model="editingSavedSearchName"
							class="rename-input"
							type="text"
							:disabled="isRenamingView"
							@click.stop
							@keydown.stop
							@keydown.enter.prevent="commitRenameSavedSearch(view)"
							@keydown.esc.prevent="cancelRenameSavedSearch"
							@blur="commitRenameSavedSearch(view)"
						/>
						<h3 v-else>{{ view.name }}</h3>
						<AppIcon v-if="activeSavedSearchId === view.id" :name="IconName.Check" class="active-icon" />
					</div>
					<p>{{ savedSearchSummary(view) }}</p>
				</div>
				<div class="actions">
					<button
						type="button"
						class="icon-button"
						:aria-label="t('custom-views.rename')"
						:title="t('custom-views.rename')"
						@click.stop="startRenameSavedSearch(view)"
						@keydown.stop
					>
						<AppIcon :name="IconName.PencilEdit" />
					</button>
					<button
						type="button"
						class="icon-button"
						:aria-label="t('custom-views.delete')"
						:title="t('custom-views.delete')"
						@click.stop="openDeleteModal(view)"
						@keydown.stop
					>
						<AppIcon :name="IconName.Trash" />
					</button>
				</div>
			</div>
		</div>

		<Teleport to="body">
			<div v-if="deleteCandidate" class="modal-overlay" @click.self="closeDeleteModal">
				<section class="delete-modal" role="dialog" aria-modal="true" aria-labelledby="delete-custom-view-title">
					<h2 id="delete-custom-view-title">{{ t("custom-views.delete-modal-title") }}</h2>
					<p>{{ t("custom-views.delete-modal-message", { name: deleteCandidate.name }) }}</p>
					<p v-if="deleteError" class="error">{{ deleteError }}</p>
					<div class="actions">
						<button type="button" class="cancel-action" :disabled="isDeletingView" @click="closeDeleteModal">
							{{ t("custom-views.cancel") }}
						</button>
						<button type="button" class="delete-action" :disabled="isDeletingView" @click="confirmDeleteSavedSearch">
							{{ isDeletingView ? t("custom-views.deleting") : t("custom-views.delete") }}
						</button>
					</div>
				</section>
			</div>
		</Teleport>
	</section>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from "vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { useI18n } from "vue-i18n"
import { storeToRefs } from "pinia"
import { useSearchContextStore } from "@/stores/searchContextStore"
import type { SearchContext } from "@/graphql"
import type { CustomView } from "@/utils/ApiClient"

const { t } = useI18n()
const searchContextStore = useSearchContextStore()
const { searchContexts, searchContextsLoading, searchContextsError, activeSearchContextId } =
	storeToRefs(searchContextStore)

type SavedSearch = CustomView | SearchContext

const savedSearches = computed<SavedSearch[]>(() => searchContexts.value)
const isLoading = computed(() => searchContextsLoading.value)
const loadError = computed(() => searchContextsError.value)
const activeSavedSearchId = computed(() => activeSearchContextId.value)

defineEmits<{
	close: []
	select: [view: SavedSearch]
}>()

const savedSearchQuery = ref("")
const editingSavedSearchId = ref<string | null>(null)
const editingSavedSearchName = ref("")
const renameInput = ref<HTMLInputElement | null>(null)
const isRenamingView = ref(false)
const deleteCandidate = ref<SavedSearch | null>(null)
const isDeletingView = ref(false)
const deleteError = ref("")

const filteredSavedSearches = computed<SavedSearch[]>(() => {
	const query = savedSearchQuery.value.trim().toLowerCase()
	if (!query) return savedSearches.value
	return savedSearches.value.filter((view: SavedSearch) => view.name.toLowerCase().includes(query))
})

function viewNumber(config: Record<string, unknown>, key: string): number | null {
	const value = config[key]
	return typeof value === "number" && Number.isFinite(value) ? value : null
}

function isSearchContext(value: SavedSearch): value is SearchContext {
	return "views" in value && "layers" in value && "display" in value
}

function countValues(value: unknown): number {
	if (Array.isArray(value)) return value.length > 0 ? 1 : 0
	if (typeof value !== "object" || value === null) return value == null ? 0 : 1

	return Object.values(value).reduce((count, item) => count + countValues(item), 0)
}

function savedSearchSummary(view: SavedSearch): string {
	if (isSearchContext(view)) {
		const filterCount = view.views.reduce((count, item) => count + countValues(item.filter), 0)
		const layerCount = view.layers.filter(layer => layer.active).length
		return t("custom-views.summary", { filters: filterCount, layers: layerCount, results: 0 })
	}

	const filterCount =
		viewNumber(view.displayConfig, "filterCount") ??
		viewNumber((view.filterConfig.metadata as Record<string, unknown> | undefined) ?? {}, "filterCount") ??
		0
	const layerCount =
		viewNumber(view.displayConfig, "layerCount") ??
		viewNumber((view.filterConfig.metadata as Record<string, unknown> | undefined) ?? {}, "layerCount") ??
		0
	const resultCount = viewNumber(view.displayConfig, "resultCount") ?? view.opportunityIds.length

	return t("custom-views.summary", { filters: filterCount, layers: layerCount, results: resultCount })
}

async function startRenameSavedSearch(view: SavedSearch): Promise<void> {
	editingSavedSearchId.value = view.id
	editingSavedSearchName.value = view.name
	await nextTick()
	renameInput.value?.focus()
	renameInput.value?.select()
}

function cancelRenameSavedSearch(): void {
	editingSavedSearchId.value = null
	editingSavedSearchName.value = ""
}

function assignRenameInput(element: unknown): void {
	renameInput.value = element instanceof HTMLInputElement ? element : null
}

async function commitRenameSavedSearch(view: SavedSearch): Promise<void> {
	if (editingSavedSearchId.value !== view.id || isRenamingView.value) return

	const name = editingSavedSearchName.value.trim()
	if (!name || name === view.name) {
		cancelRenameSavedSearch()
		return
	}

	isRenamingView.value = true
	try {
		await searchContextStore.updateSearchContextName(view.id, name)
		cancelRenameSavedSearch()
	} catch (error) {
		console.error(error)
	} finally {
		isRenamingView.value = false
	}
}

function openDeleteModal(view: SavedSearch): void {
	deleteCandidate.value = view
	deleteError.value = ""
}

function closeDeleteModal(): void {
	if (isDeletingView.value) return
	deleteCandidate.value = null
	deleteError.value = ""
}

async function confirmDeleteSavedSearch(): Promise<void> {
	if (!deleteCandidate.value) return

	isDeletingView.value = true
	deleteError.value = ""
	try {
		await searchContextStore.deleteSearchContext(deleteCandidate.value.id)
		deleteCandidate.value = null
	} catch (error) {
		deleteError.value = error instanceof Error ? error.message : String(error)
	} finally {
		isDeletingView.value = false
	}
}
</script>

<style scoped>
.saved-searches-panel {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-md);
	margin: 0 calc(var(--spacing-xl) * -1) 0;

	.header {
		display: grid;
		grid-template-columns: auto 1fr auto;
		align-items: center;
		gap: var(--spacing-sm);
		padding: 0 var(--spacing-xl) var(--spacing-md);

		h2 {
			margin: 0;
			font-size: var(--font-size-base);
			font-weight: var(--font-weight-semibold);
			color: var(--neutral-900);
		}
	}

	.back-button {
		border: none;
		background: transparent;
		color: var(--neutral-600);
		cursor: pointer;
		display: inline-flex;
		align-items: center;
		justify-content: center;

		svg {
			width: var(--size-icon-md);
			height: var(--size-icon-md);
			fill: none;
			stroke: currentColor;
			stroke-width: 2;
			stroke-linecap: round;
			stroke-linejoin: round;
		}
	}

	.count-pill {
		border-radius: var(--border-radius-full);
		background: var(--neutral-50);
		color: var(--neutral-600);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-medium);
		padding: var(--spacing-xxs) var(--spacing-sm);
	}

	.search-input {
		position: relative;
		margin: 0 var(--spacing-xl);
		display: flex;
		align-items: center;

		svg {
			position: absolute;
			left: var(--spacing-sm);
			width: var(--space-14);
			height: var(--space-14);
			fill: none;
			stroke: var(--neutral-600);
			stroke-width: 2;
			stroke-linecap: round;
			stroke-linejoin: round;
			pointer-events: none;
		}

		input {
			width: 100%;
			height: var(--spacing-xl);
			padding: 0 var(--spacing-sm) 0 calc(var(--spacing-sm) * 2 + var(--space-14));
			border: var(--border-width-thin) solid var(--neutral-400);
			border-radius: var(--border-radius-full);
			font-size: var(--font-size-small);
			font-family: var(--font-family-primary);
			background: var(--neutral-0);
			color: var(--neutral-900);
			outline: none;
			box-sizing: border-box;

			&:focus {
				border-color: var(--neutral-900);
			}
		}
	}

	.list {
		display: flex;
		flex-direction: column;
	}

	.item {
		width: 100%;
		display: grid;
		grid-template-columns: 1fr auto;
		align-items: center;
		gap: var(--spacing-md);
		border: none;
		border-bottom: var(--border-width-thin) solid var(--neutral-200);
		background: var(--neutral-0);
		padding: var(--spacing-md) var(--spacing-xl);
		text-align: left;
		cursor: pointer;
		outline: none;

		&.active {
			border-left: var(--border-width-base) solid var(--primary-green);
			padding-left: calc(var(--spacing-xl) - var(--border-width-base));
		}

		&:focus-visible {
			box-shadow: inset 0 0 0 2px var(--primary-green);
		}
	}

	.main {
		min-width: 0;

		.title-row {
			display: flex;
			align-items: center;
			gap: var(--spacing-xs);
		}

		h3 {
			margin: 0;
			color: var(--neutral-900);
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-semibold);
			line-height: var(--line-height-tight);
			overflow-wrap: anywhere;
		}

		.active-icon {
			flex: 0 0 auto;
			width: var(--size-icon-sm);
			height: var(--size-icon-sm);
			fill: none;
			stroke: var(--primary-green);
			stroke-width: 2;
			stroke-linecap: round;
			stroke-linejoin: round;
		}

		p {
			margin: var(--spacing-xs) 0 0;
			color: var(--neutral-600);
			font-size: var(--font-size-xs);
		}
	}

	.rename-input {
		width: 100%;
		border: var(--border-width-thin) solid var(--primary-green);
		border-radius: var(--border-radius-sm);
		background: var(--neutral-0);
		color: var(--neutral-900);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-small);
		font-weight: var(--font-weight-semibold);
		line-height: var(--line-height-tight);
		padding: var(--spacing-xs) var(--spacing-sm);
		outline: none;
	}

	.actions {
		display: flex;
		gap: var(--spacing-sm);

		.icon-button {
			width: var(--size-button-s);
			height: var(--size-button-s);
			border: none;
			background: transparent;
			color: var(--neutral-600);
			cursor: pointer;
			padding: var(--spacing-xs);
			display: inline-flex;
			align-items: center;
			justify-content: center;

			&:hover {
				color: var(--neutral-900);
			}

			svg {
				width: var(--size-icon-md);
				height: var(--size-icon-md);
				fill: none;
				stroke: currentColor;
				stroke-width: 1.8;
				stroke-linecap: round;
				stroke-linejoin: round;
			}
		}
	}

	.state-message,
	.error-message {
		margin: 0 var(--spacing-xl);
		color: var(--neutral-600);
		font-size: var(--font-size-small);
	}

	.error-message {
		color: var(--accent-warning);
	}
}

.modal-overlay {
	position: fixed;
	inset: 0;
	z-index: 1000;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(25, 23, 22, 0.45);
	padding: var(--spacing-xl);

	.delete-modal {
		width: min(520px, 100%);
		border-radius: var(--border-radius-lg);
		background: var(--neutral-0);
		box-shadow: 0 18px 50px rgba(25, 23, 22, 0.2);
		padding: var(--spacing-xl);

		h2 {
			margin: 0;
			color: var(--neutral-900);
			font-size: var(--font-size-2xl);
			font-weight: var(--font-weight-semibold);
			line-height: var(--line-height-tight);
		}

		p {
			margin: var(--spacing-lg) 0 0;
			color: var(--neutral-600);
			font-size: var(--font-size-medium);
			line-height: var(--line-height-normal);
		}

		.error {
			color: var(--accent-warning);
		}

		.actions {
			display: flex;
			justify-content: flex-end;
			gap: var(--spacing-sm);
			margin-top: var(--spacing-xl);
		}

		.cancel-action,
		.delete-action {
			border-radius: var(--border-radius-full);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-medium);
			font-weight: var(--font-weight-semibold);
			padding: var(--spacing-sm) var(--spacing-lg);
			cursor: pointer;

			&:disabled {
				cursor: not-allowed;
				opacity: 0.65;
			}
		}

		.cancel-action {
			border: var(--border-width-thin) solid var(--neutral-400);
			background: var(--neutral-0);
			color: var(--neutral-900);
		}

		.delete-action {
			border: var(--border-width-thin) solid var(--accent-warning);
			background: var(--accent-warning);
			color: var(--neutral-0);
		}
	}
}
</style>
